import {
  Either, Maybe, Validation,
} from 'monet';
import { getISODateStringByDate } from '@/mixins/dateMixin';
import { makeStatusBySchema } from '@/storage/gateways/EgiszIemkGateway';
import {
  DISCOUNT_IN_KOPECK,
  DISCOUNT_IN_PERCENT,
} from '@/components/SwitchDiscountType.vue';
import LeftResponse from '@/storage/LeftResponse';
import ServicePriceRubles from '@/storage/ServicePriceRubles';

const DISCOUNT_IN_KOPECK_ROUNDED = 'kopeck-rounded';
const DISCOUNT_IN_PERCENT_ROUNDED = 'percent-rounded';

/**
 * @description Дневник приема.
 * @param {string}      complaints
 * @param {string}      dentalAnamnesis
 * @param {string}      diagnosis
 * @param {string}      diseaseOutcome
 * @param {string}      epicrisis
 * @param {string}      generalAnalysis
 * @param {string}      guarantees
 * @param {number|null} icdId
 * @param {string}      icdName
 * @param {boolean}     isFiled
 * @param {string}      objectively
 * @param {string}      prognosis
 * @param {string}      recommendations
 * @param {string}      severityCondition
 * @param {string}      statusLokalis
 * @param {string}      studies
 * @param {string}      treatment
 * @param {number|null} templateId
 * @param {number|null} id
 * @param {string}      description
 * @param {number[]}    teeth
 * @param {any[]}       dentalParts
 * @constructor
 */
export function Diary(
  complaints = '',
  dentalAnamnesis = '',
  diagnosis = '',
  diseaseOutcome = '',
  epicrisis = '',
  generalAnalysis = '',
  guarantees = '',
  icdId = null,
  icdName = '',
  isFiled = false,
  objectively = '',
  prognosis = '',
  recommendations = '',
  severityCondition = '',
  statusLokalis = '',
  studies = '',
  treatment = '',
  templateId = null,
  id = null,
  description = '',
  teeth = [],
  dentalParts = [],
) {
  this.complaints = complaints;
  this.dentalAnamnesis = dentalAnamnesis;
  this.diagnosis = diagnosis;
  this.diseaseOutcome = diseaseOutcome || 'recovery';
  this.epicrisis = epicrisis;
  this.generalAnalysis = generalAnalysis;
  this.guarantees = guarantees;
  this.icdId = icdId;
  this.isFiled = isFiled;
  this.objectively = objectively;
  this.prognosis = prognosis;
  this.recommendations = recommendations;
  this.severityCondition = severityCondition || 'normal';
  this.statusLokalis = statusLokalis;
  this.studies = studies;
  this.treatment = treatment;
  this.templateId = templateId;
  this.id = id;
  this.description = description;
  this.teeth = teeth;
  this.dentalParts = dentalParts;

  this.appendPhraseIntoField = (property, phrase) => {
    this[property] = `${this[property]} ${phrase}`;
    return this;
  };

  let initial = JSON.stringify(this);
  this.refresh = () => {
    initial = JSON.stringify(this);
  };
  this.getIcd = () => ({ id: icdId, name: icdName });
  this.isChanged = () => JSON.stringify(this) !== initial;
}

/**
 * @description Диагноз части зуба.
 * @param {number} id    Идентификатор.
 * @param {string} name  Название.
 * @param {string} color Цвет.
 * @constructor
 */
export function Diagnosis(id, name, color) {
  this.id = id;
  this.name = name;
  this.color = color;
}
/**
 * @description Категория услуг.
 *
 * @param id
 * @param name
 * @param services
 * @param children
 * @param stages
 * @constructor
 */
export function Category(id, name, services, children, stages) {
  this.id = id;
  this.name = name;
  this.services = services;
  this.children = children;
  this.hasStages = stages?.length > 0;
  this.stages = stages?.map((stage) => ({
    id: stage.id,
    name: stage.name,
    services: stage.services,
  }));

  /**
   * @description  Есть ли хотя бы одна услуги.
   * @returns {boolean}
   */
  this.isHasServices = () => this
    .services
    .length > 0
    || this
      .children
      .some((item) => item.services.length > 0);
}
/**
 * @description Прайс лист.
 * @param {number} id           Идентификатор.
 * @param {string} name         Название.
 * @param {array}  categoryList Категории.
 * @constructor
 */
export function PriceList(id, name, categoryList) {
  this.id = id;
  this.name = name;
  this.categoryList = categoryList.map((category) => (
    new Category(
      category.id,
      category.name,
      category.services,
      category.children,
      category.stages,
    )
  ));
  /**
   * Есть ли хотя бы одна услуги.
   *
   * @returns {boolean}
   */
  this.isHasServices = () => this
    .categoryList
    .some(
      (category) => category.isHasServices(),
    );
}
/**
 * @description Часть зуба.
 * @param {string}      id        Идентификатор.
 * @param {string}      name      Название.
 * @param {Diagnosis[]} diagnoses Диагнозы.
 * @constructor
 */
export function Part(id, name, diagnoses) {
  this.id = id;
  this.name = name;
  this.diagnoses = diagnoses;

  this.toRequest = () => ({
    part_id: id,
    diagnoses: this.diagnoses.map((item) => item.id),
  });
}

/**
 * @description Зуб.
 * @param {number} number Номер зуба.
 * @param {Part[]} parts  Части зуба.
 */
export function Tooth(number, parts) {
  this.number = number;
  this.parts = parts;
  this.toRequest = () => ({
    number,
    parts: this.parts.map((item) => item.toRequest()),
  });
}

/**
 * @description Запрос на печать документов.
 * @param {int}   visitId
 * @param {array} documents
 * @constructor
 */
export function PrintDocumentsRequest(
  visitId,
  documents,
) {
  this.documents = documents;
  this.visitId = visitId;

  this.getVisitId = () => (this.visitId);

  this.toQuery = () => ({
    documents: this.documents.map((document) => ({
      id: document.id,
      count: document.count,
      number: document.number,
      date: document.date,
    })),
  });
}

/**
 * @description Запрос на скачивание документа.
 * @param {int}   visitId
 * @param {any[]} documents
 * @constructor
 */
export function DownloadDocumentRequest(visitId, documents) {
  this.getVisitId = () => visitId;

  this.toQuery = () => ({
    documents: documents.map((document) => ({
      id: document.id,
      number: document.number,
      date: document.date,
    })),
  });
}

/**
 * @description Зубная формула.
 * @param {Tooth[]} teeth Зубы.
 */
export function DentalFormula(teeth = []) {
  this.teeth = teeth;
}

/**
 *
 * @param {Tooth[]} defaultDentalFormula
 * @returns {Tooth[]}
 */
export function DentalFormulaFactory(defaultDentalFormula) {
  this.make = () => Object.fromEntries(
    defaultDentalFormula.map((tooth) => [
      tooth.number,
      new Tooth(
        tooth.number,
        tooth.parts.map(
          (part) => new Part(
            part.id,
            part.name,
            part.diagnoses.map(
              (diagnosis) => new Diagnosis(diagnosis.id, diagnosis.name, diagnosis.color),
            ),
          ),
        ),
      ),
    ]),
  );
}

/**
 * @description Запрос на создание шаблона протокола лечения.
 * @param {number|null} id
 * @param {string} name
 * @param {number|null} categoryId
 * @param {string} complaints
 * @param {string} dentalAnamnesis
 * @param {string} diagnosis
 * @param {string} generalAnalysis
 * @param {string} guarantees
 * @param {number|null} icdId
 * @param {string} objectively
 * @param {string} prognosis
 * @param {string} recommendations
 * @param {string} statusLokalis
 * @param {string} studies
 * @param {string} treatment
 * @param {array} teeth
 * @param {array} dentalParts
 * @param {boolean} sanitized
 * @constructor
 */
export function DiaryTemplateCreationRequest(
  id = null,
  name = '',
  categoryId = null,
  complaints = '',
  dentalAnamnesis = '',
  diagnosis = '',
  generalAnalysis = '',
  guarantees = '',
  icdId = null,
  objectively = '',
  prognosis = '',
  recommendations = '',
  statusLokalis = '',
  studies = '',
  treatment = '',
  teeth = [],
  dentalParts = [],
  sanitized = false,
) {
  this.id = id;
  this.name = name;
  this.categoryId = categoryId;
  this.complaints = complaints;
  this.dentalAnamnesis = dentalAnamnesis;
  this.diagnosis = diagnosis;
  this.generalAnalysis = generalAnalysis;
  this.guarantees = guarantees;
  this.icdId = icdId;
  this.objectively = objectively;
  this.prognosis = prognosis;
  this.recommendations = recommendations;
  this.statusLokalis = statusLokalis;
  this.studies = studies;
  this.treatment = treatment;
  this.teeth = teeth;
  this.dentalParts = dentalParts;
  this.sanitized = sanitized;

  this.getId = () => this.id;

  this.toQuery = () => ({
    name: this.name,
    category_id: this.categoryId,
    complaints: this.complaints,
    dental_anamnesis: this.dentalAnamnesis,
    diagnosis: this.diagnosis,
    general_analysis: this.generalAnalysis,
    guarantees: this.guarantees,
    icd_id: this.icdId,
    objectively: this.objectively,
    prognosis: this.prognosis,
    recommendations: this.recommendations,
    status_lokalis: this.statusLokalis,
    studies: this.studies,
    treatment: this.treatment,
    teeth: this.teeth,
    dental_parts: this.dentalParts,
    sanitized: this.sanitized,
  });
}

/**
 * @description Таблица услуг.
 * @param {Position[]} rows
 * @constructor
 */
export function ServiceTable(rows = []) {
  /**
   * @description Позиции.
   * @type {Position[]}
   */
  this.rows = rows;

  /**
   * @description Привести к запросу.
   * @returns {Object}
   */
  this.toQuery = () => ({
    services: this.rows.map((row) => row.toQuery()),
  });

  /**
   * @description Привести к строке.
   * @returns {string}
   */
  const toString = () => JSON.stringify(
    this.rows.map((row) => row.getMeaningfulFields()),
  );

  /**
   * @description Начальное значение.
   * @type {string}
   */
  let initial = toString();

  /**
   * @description Обновить.
   * @returns {void}
   */
  this.refresh = () => {
    initial = toString();
  };

  /**
   * @description Изменено.
   * @returns {boolean}
   */
  this.isChanged = () => toString() !== initial;
}

/**
 * @description Счета.
 * @param {VisitInvoice[]} rows
 * @constructor
 */
export function Invoices(rows = []) {
  /**
   * @description Позиции.
   * @type {VisitInvoice[]}
   */
  this.rows = rows;

  /**
   * @description Привести к запросу.
   * @returns {Object}
   */
  this.toQuery = () => ({
    invoices: this.rows.map((row) => row.toQuery()),
  });

  /**
   * @description Привести к строке.
   * @returns {string}
   */
  const toString = () => JSON.stringify(
    this.rows.map((row) => row.getMeaningfulFields()),
  );

  /**
   * @description Начальное значение.
   * @type {string}
   */
  let initial = toString();

  /**
   * @description Обновить.
   * @returns {void}
   */
  this.refresh = () => {
    initial = toString();
  };

  /**
   * @description Изменено.
   * @returns {boolean}
   */
  this.isChanged = () => toString() !== initial;
}

/**
 * @description Строка заказ-наряда.
 * @constructor
 * @param {number}   id
 * @param {string}   serviceName
 * @param {number[]} teeth
 * @param {any[]}    dentalParts
 * @param {number}   count
 * @param {number}   price
 * @param {string}   vita
 */
export function WorkOrderRow(
  id = 0,
  serviceName = '',
  teeth = [],
  dentalParts = [],
  count = 1.0,
  price = 0,
  vita = '',
) {
  this.id = id;
  this.serviceName = serviceName;
  this.teeth = teeth;
  this.dentalParts = dentalParts;
  this.count = count;
  this.price = price;
  this.vita = vita;

  /**
   * @description Привести к запросу.
   * @returns {Object}
   */
  this.toQuery = () => ({
    id: this.id,
    serviceName: this.serviceName,
    teeth: this.teeth,
    dentalParts: this.dentalParts.map(({ value }) => value),
    count: this.count,
    price: this.price,
    vita: this.vita,
  });
}

/**
 * @description Таблица заказ-наряда.
 * @param {WorkOrderRow[]} rows
 * @constructor
 */
export function WorkOrderTable(rows = []) {
  /**
   * @description Строки.
   * @type {WorkOrderRow[]}
   */
  this.rows = rows;

  /**
   * @description Добавить пустую строку.
   * @type {void}
   */
  this.addRow = () => { this.rows.push(new WorkOrderRow()); };

  /**
   * @description Привести к строке.
   * @returns {string}
   */
  const toString = () => JSON.stringify(this.rows);

  /**
   * @description Начальное значение.
   * @type {string}
   */
  let initial = toString();

  /**
   * @description Обновить.
   * @returns {void}
   */
  this.refresh = () => {
    initial = toString();
  };

  /**
   * @description Изменено.
   * @returns {boolean}
   */
  this.isChanged = () => toString() !== initial;

  /**
   * @description Привести к запросу.
   * @returns {Object}
   */
  this.toQuery = () => ({
    workOrderTable: this.rows.map((row) => row.toQuery()),
  });
}

/**
 * @description Привести длительность услуги к формату hh:mm.
 * @param {string} duration Длительность.
 * @returns {string}
 */
const formatDuration = (duration) => {
  const parts = duration.split(':');
  const [hour, min] = parts;
  if (parts.length === 2) {
    return `${hour.padStart(2, '0')}:${min.padStart(2, '0')}`;
  }
  return `${hour.padStart(2, '0')}:00`;
};

/**
 * @description Скидка позиции.
 * @param {string} type
 * @param {number} value
 * @constructor
 */
export function PositionDiscount(type, value) {
  this.type = type;
  this.value = value;

  /**
   * @description Применить скидку.
   * @param {ServicePriceRubles} amount Сумма.
   * @returns {{discount:ServicePriceRubles,final:ServicePriceRubles}}
   */
  this.apply = (amount) => {
    if (type === DISCOUNT_IN_KOPECK) {
      const discount = new ServicePriceRubles(value);
      const final = amount.sub(discount);
      return { discount, final };
    }
    const final = amount.getPercent(100 - value);
    const discount = amount.sub(final);
    return { discount, final };
  };

  /**
   * @description Установить значение.
   * @param {number} newValue
   * @returns {PositionDiscount}
   */
  this.setValue = (newValue) => new this.constructor(type, newValue);

  /**
   * @description Получить значение.
   * @returns {number}
   */
  this.getValue = () => value;

  /**
   * @description Получить тип скидки.
   * @returns {string}
   */
  this.getType = () => type;

  /**
   * @description Получить значимые поля.
   * @returns {Object}
   */
  this.getMeaningfulFields = () => ({ type, value });

  /**
   * @description Перевести в копейки.
   * @param {number} kop Копейки.
   * @returns {PositionDiscount}
   */
  this.convertToKop = (kop) => {
    if (type === DISCOUNT_IN_KOPECK) {
      return this;
    }
    const newValue = Math.floor((kop / 100) * value);
    return new this.constructor(
      DISCOUNT_IN_KOPECK,
      Math.round(newValue / 100) * 100,
    );
  };

  /**
   * @description Перевести в проценты.
   * @param {number} kop Копейки.
   * @returns {PositionDiscount}
   */
  this.convertToPercent = (kop) => {
    if (type === DISCOUNT_IN_PERCENT) {
      return this;
    }
    const newValue = value === 0
      ? 0
      : (1 / (kop / value)) * 100;
    return new this.constructor(DISCOUNT_IN_PERCENT, Math.round(newValue));
  };

  this.getTypeToQuery = () => {
    if (type === DISCOUNT_IN_KOPECK) {
      return DISCOUNT_IN_KOPECK_ROUNDED;
    }
    return DISCOUNT_IN_PERCENT_ROUNDED;
  };
}

PositionDiscount.default = () => new PositionDiscount(DISCOUNT_IN_KOPECK, 0);

export function PositionDiscountWithoutRound(type, value) {
  PositionDiscount.call(this, type, value);

  /**
   * @description Применить скидку.
   * @param {ServicePriceRubles} amount Сумма.
   * @returns {{discount:ServicePriceRubles,final:ServicePriceRubles}}
   */
  this.apply = (amount) => {
    const discount = type === DISCOUNT_IN_KOPECK
      ? new ServicePriceRubles(value)
      : amount.getPercentWithoutRound(value);
    const final = amount.sub(discount);
    return { discount, final };
  };

  this.getTypeToQuery = () => type;
}

/**
 * @description Поля позиции.
 * @param {boolean}          byVHIPolicy
 * @param {string}           categoryName
 * @param {number}           count
 * @param {boolean}          complaint
 * @param {any[]}            dentalParts
 * @param {PositionDiscount} discount
 * @param {string|number}    duration
 * @param {number}           cost
 * @param {?number}          serviceId
 * @param {string}           serviceName
 * @param {number[]}         teeth
 * @param {string}           units
 * @param {string}           key
 * @constructor
 */
export function PositionProps(
  byVHIPolicy,
  categoryName,
  count,
  complaint,
  dentalParts,
  discount,
  duration,
  cost,
  serviceId,
  serviceName,
  teeth,
  units,
  key,
  paidStatus = '',
  hasEditingRestrict = false,
) {
  this.byVHIPolicy = byVHIPolicy;
  this.categoryName = categoryName;
  this.count = count;
  this.complaint = complaint;
  this.dentalParts = dentalParts;
  this.discount = discount;
  this.duration = duration;
  this.cost = cost;
  this.serviceId = serviceId;
  this.serviceName = serviceName;
  this.teeth = teeth;
  this.units = units;
  this.key = key;
  this.paidStatus = paidStatus;
  this.hasEditingRestrict = hasEditingRestrict;

  /**
   * @description Сумма.
   * @type {ServicePriceRubles}
   */
  this.amount = new ServicePriceRubles(this.cost).mul(this.count);
  /**
   * @description По полной скидке.
   * @type {boolean}
   */
  this.isFullDiscounted = this.byVHIPolicy || this.complaint;
  if (this.isFullDiscounted) {
    this.discountAmount = this.amount;
    this.finalPrice = ServicePriceRubles.zero();
  } else {
    ({
      discount: this.discountAmount,
      final: this.finalPrice,
    } = this.discount.apply(this.amount));
  }

  /**
   * @description Получить значимые поля.
   * @returns {Object}
   */
  this.getMeaningfulFields = () => ({
    byVHIPolicy: this.byVHIPolicy,
    count: this.count,
    complaint: this.complaint,
    dentalParts: this.dentalParts.map(({ value }) => value),
    discount: this.discount.getMeaningfulFields(),
    duration: this.duration,
    cost: this.cost,
    serviceId: this.serviceId,
    teeth: this.teeth,
    units: this.units,
  });
}

/**
 * @description Зуб.
 * @param {number} number
 * @param {string} upper
 * @param {string} lower
 */
export function DentalCavitiesTooth(number, upper, lower) {
  this.number = number;
  this.upper = upper;
  this.lower = lower;
}

/**
 * @description Полость рта пациента.
 * @param {DentalCavitiesTooth[]} teeth Зубы.
 * @constructor
 */
export function DentalCavities(teeth = []) {
  /**
   * @type {DentalCavitiesTooth[]}
   */
  this.teeth = teeth;
  let start = JSON.stringify(this);
  this.refresh = () => {
    start = JSON.stringify(this);
  };
  this.isChanged = () => JSON.stringify(this) !== start;
}

/**
 * @description Запрос на редактирование полости рта пациента.
 * @param {DentalCavities} dentalCavities
 * @param {numeric}        visitId
 */
export function EditPatientDentalCavitiesRequest(dentalCavities, visitId) {
  const teeth = dentalCavities.teeth.map((tooth) => ({
    number: tooth.number,
    diagnoses: {
      upper: tooth.upper,
      lower: tooth.lower,
    },
  }));
  this.getVisitId = () => visitId;
  this.toQuery = () => ({ teeth });
}

/**
 *
 * @param {number|null}  id
 * @param {string}       discountType
 * @param {ServiceTable} serviceTable
 * @constructor
 */
export function VisitInvoice(
  id = null,
  discountType = DISCOUNT_IN_KOPECK,
  serviceTable = new ServiceTable(),
) {
  this.id = id;
  this.serviceTable = serviceTable;
  this.discountType = discountType;

  this.toQuery = () => ({
    id,
    services: this.serviceTable.rows.map((row) => row.toQuery()),
  });

  /**
   * @description Получить значимые поля.
   * @returns {Object}
   */
  this.getMeaningfulFields = () => ({
    discountType: this.discountType,
    serviceTable: this.serviceTable.rows.map((row) => row.getMeaningfulFields()),
  });
}

/**
 * @description Позиция визита.
 * @param {boolean}          byVHIPolicy
 * @param {string}           categoryName
 * @param {number}           count
 * @param {boolean}          complaint
 * @param {any[]}            dentalParts
 * @param {PositionDiscount} discount
 * @param {string|number}    duration
 * @param {?number}          id
 * @param {number}           cost
 * @param {?number}          serviceId
 * @param {string}           serviceName
 * @param {number[]}         teeth
 * @param {string}           units
 * @param {string}           key
 * @param {string}           paidStatus
 * @param {boolean}          hasEditingRestrict
 * @constructor
 */
export function VisitPosition(
  byVHIPolicy = false,
  categoryName = '',
  count = 1,
  complaint = false,
  dentalParts = [],
  discount = new PositionDiscount(DISCOUNT_IN_KOPECK, 0),
  duration = 0,
  id = null,
  cost = 0,
  serviceId = null,
  serviceName = '',
  teeth = [],
  units = '',
  key = '',
  paidStatus = '',
  hasEditingRestrict = false,
) {
  PositionProps.call(
    this,
    byVHIPolicy,
    categoryName,
    count,
    complaint,
    dentalParts,
    discount,
    duration,
    cost,
    serviceId,
    serviceName,
    teeth,
    units,
    key,
    paidStatus,
    hasEditingRestrict,
  );
  this.id = id;

  /**
   * @description Создать копию c полями.
   * @param {PositionProps} props Поля.
   * @returns {Object}
   */
  this.copyWithProps = (props) => new this.constructor(
    props.byVHIPolicy,
    props.categoryName,
    props.count,
    props.complaint,
    props.dentalParts,
    props.discount,
    props.duration,
    this.id,
    props.cost,
    props.serviceId,
    props.serviceName,
    props.teeth,
    props.units,
    props.key,
    props.paidStatus,
    props.hasEditingRestrict,
  );
  const copyWith = (props) => this.copyWithProps({
    byVHIPolicy: props.byVHIPolicy === undefined ? this.byVHIPolicy : props.byVHIPolicy,
    categoryName: this.categoryName,
    count: props.count === undefined ? this.count : props.count,
    complaint: props.complaint === undefined ? this.complaint : props.complaint,
    dentalParts: props.dentalParts === undefined ? this.dentalParts : props.dentalParts,
    discount: props.discount === undefined ? this.discount : props.discount,
    duration: props.duration === undefined ? this.duration : props.duration,
    cost: this.cost,
    serviceId: this.serviceId,
    serviceName: this.serviceName,
    teeth: props.teeth === undefined ? this.teeth : props.teeth,
    units: props.units === undefined ? this.units : props.units,
    key: this.key,
    paidStatus: props.paidStatus === undefined ? this.paidStatus : props.paidStatus,
    hasEditingRestrict: props.hasEditingRestrict === undefined
      ? this.hasEditingRestrict
      : props.hasEditingRestrict,
  });
  this.setByVhiPolicy = (newByVhiPolicy) => copyWith({ byVHIPolicy: newByVhiPolicy });
  this.setComplaint = (newComplaint, newByVhiPolicy) => copyWith({
    complaint: newComplaint,
    byVHIPolicy: newByVhiPolicy,
  });
  this.setCount = (newCount) => copyWith({ count: newCount });
  this.setDiscount = (newDiscount) => copyWith({ discount: newDiscount });
  this.setTeeth = (newTeeth, newDentalParts) => copyWith({
    teeth: newTeeth,
    dentalParts: newDentalParts,
  });

  /**
   * @description Привести к запросу.
   * @returns {Object}
   */
  this.toQuery = () => ({
    by_vhi_policy: this.byVHIPolicy,
    code_icd: this.code,
    count: this.count,
    complaint: this.complaint,
    dental_parts: this.dentalParts.map(({ value }) => value),
    discount: this.discount.getValue(),
    discount_type: this.discount.getTypeToQuery(),
    duration: formatDuration(this.duration.toString()),
    id: this.id,
    price: this.cost,
    service_id: this.serviceId,
    teeth: this.teeth,
    units: this.units,
  });
}

/**
 * @description Позиция визита добавленная из плана.
 * @param {boolean}          byVHIPolicy
 * @param {string}           categoryName
 * @param {any[]}            dentalParts
 * @param {PositionDiscount} discount
 * @param {string|number}    duration
 * @param {?number}          id
 * @param {number}           price
 * @param {?number}          serviceId
 * @param {string}           serviceName
 * @param {number[]}         teeth
 * @param {string}           units
 * @param {string}           key
 * @constructor
 */
export function VisitPositionFromTreatmentPlan(
  byVHIPolicy,
  categoryName,
  count,
  complaint,
  dentalParts,
  discount,
  duration,
  treatmentPlanServiceId,
  price,
  serviceId,
  serviceName,
  teeth,
  units,
  key,
) {
  PositionProps.call(
    this,
    byVHIPolicy,
    categoryName,
    count,
    complaint,
    dentalParts,
    discount,
    duration,
    price,
    serviceId,
    serviceName,
    teeth,
    units,
    key,
  );

  this.treatmentPlanServiceId = treatmentPlanServiceId;

  /**
   * @description Создать копию c полями.
   * @param {PositionProps} props Поля.
   * @returns {Object}
   */
  this.copyWithProps = (props) => new this.constructor(
    props.byVHIPolicy,
    props.categoryName,
    props.count,
    props.complaint,
    props.dentalParts,
    props.discount,
    props.duration,
    this.treatmentPlanServiceId,
    props.cost,
    props.serviceId,
    props.serviceName,
    props.teeth,
    props.units,
    props.key,
  );
  const copyWith = (props) => this.copyWithProps({
    byVHIPolicy: props.byVHIPolicy === undefined ? this.byVHIPolicy : props.byVHIPolicy,
    categoryName: this.categoryName,
    count: props.count === undefined ? this.count : props.count,
    complaint: props.complaint === undefined ? this.complaint : props.complaint,
    dentalParts: props.dentalParts === undefined ? this.dentalParts : props.dentalParts,
    discount: props.discount === undefined ? this.discount : props.discount,
    duration: props.duration === undefined ? this.duration : props.duration,
    cost: this.cost,
    serviceId: this.serviceId,
    serviceName: this.serviceName,
    teeth: props.teeth === undefined ? this.teeth : props.teeth,
    units: props.units === undefined ? this.units : props.units,
    key: this.key,
  });
  this.setByVhiPolicy = (newByVhiPolicy) => copyWith({ byVHIPolicy: newByVhiPolicy });
  this.setComplaint = (newComplaint, newByVhiPolicy) => copyWith({
    complaint: newComplaint,
    byVHIPolicy: newByVhiPolicy,
  });
  this.setCount = (newCount) => copyWith({ count: newCount });
  this.setDiscount = (newDiscount) => copyWith({ discount: newDiscount });
  this.setTeeth = (newTeeth, newDentalParts) => copyWith({
    teeth: newTeeth,
    dentalParts: newDentalParts,
  });

  /**
   * @description Привести к запросу.
   * @returns {Object}
   */
  this.toQuery = () => ({
    by_vhi_policy: this.byVHIPolicy,
    count: this.count,
    complaint: this.complaint,
    dental_parts: this.dentalParts.map(({ value }) => value),
    discount: this.discount.getValue(),
    discount_type: this.discount.getTypeToQuery(),
    duration: formatDuration(this.duration.toString()),
    treatment_plan_service_id: this.treatmentPlanServiceId,
    price: this.cost,
    service_id: this.serviceId,
    teeth: this.teeth,
    units: this.units,
  });
}

/**
 * @description Статус проверки главным врачом.
 * @param {string} id    Идентификатор.
 * @param {string} note  Примечание.
 * @param {string} title Заголовок.
 * @constructor
 */
function ChiefPhysicianStatus(id, note, title) {
  /**
   * @description Получить идентификатор.
   * @returns {string}
   */
  this.getId = () => id;
  /**
   * @description Получить поясняющий текст.
   * @returns {string}
   */
  this.getNote = () => note;
  /**
   * @description Получить заголовок.
   * @returns {string}
   */
  this.getTitle = () => title;
}

export const makeChiefStatusBySchema = (raw) => new ChiefPhysicianStatus(
  raw.id,
  raw.note,
  raw.title,
);

export const makeDiscount = (type, value) => {
  if (type === DISCOUNT_IN_KOPECK_ROUNDED) {
    return new PositionDiscount(DISCOUNT_IN_KOPECK, value);
  }
  if (type === DISCOUNT_IN_PERCENT_ROUNDED) {
    return new PositionDiscount(DISCOUNT_IN_PERCENT, value);
  }
  return new PositionDiscountWithoutRound(type, value);
};

/**
 * @description Создать таблицу услуг по схеме.
 * @param {Object[]} rows Строки.
 * @returns {ServiceTable}
 */
export const makeServiceTableByScheme = (rows) => new ServiceTable(
  rows.map(
    (item) => new VisitPosition(
      item.by_vhi_policy,
      item.category_name,
      item.count,
      item.complaint,
      item.dental_parts || [],
      makeDiscount(item.discount_type, item.discount_value),
      item.duration,
      item.id,
      item.price,
      item.service_id,
      item.service_name,
      item.teeth,
      item.units,
      '',
      item.paid_status,
      item.has_editing_restrict,
    ),
  ),
);

/**
 * @description Создать список визитов по схеме.
 * @param {Object[]} rows Строки.
 * @returns {Invoices}
 */
export const makeInvoicesByScheme = (rows) => new Invoices(
  rows.map((item) => new VisitInvoice(
    item.id,
    item.discount_type,
    makeServiceTableByScheme(item.services),
  )),
);

export const makeDiaryByScheme = (raw) => new Diary(
  raw.complaints,
  raw.dental_anamnesis,
  raw.diagnosis,
  raw.disease_outcome,
  raw.epicrisis,
  raw.general_analysis,
  raw.guarantees,
  raw.icd_id,
  raw.icd_name,
  raw.is_filed,
  raw.objectively,
  raw.prognosis,
  raw.recommendations,
  raw.severity_condition,
  raw.status_lokalis,
  raw.studies,
  raw.treatment,
  raw.template_id,
  raw.id,
  raw.description,
  raw.teeth,
  raw.dental_parts,
);

export const makeDentalFormulaTeeth = (raw) => Object.fromEntries(
  raw.map((tooth) => [
    tooth.number,
    new Tooth(
      tooth.number,
      tooth.parts.map(
        (part) => new Part(
          part.id,
          part.name,
          part.diagnoses.map(
            (diagnosis) => new Diagnosis(diagnosis.id, diagnosis.name, diagnosis.color),
          ),
        ),
      ),
    ),
  ]),
);

export const makeDentalCavities = (teeth) => new DentalCavities(
  teeth.map(
    (tooth) => new DentalCavitiesTooth(tooth.number, tooth.diagnoses.upper, tooth.diagnoses.lower),
  ),
);

/**
 * @description Запрос на создание дневника приема.
 * @param {number} visitId Идентификатор визита.
 * @param {Diary}  diary   Дневник приема.
 */
export function CreateVisitDiaryRequest(visitId, diary) {
  this.getVisitId = () => visitId;
  this.toQuery = () => ({
    complaints: diary.complaints,
    dental_anamnesis: diary.dentalAnamnesis,
    diagnosis: diary.diagnosis,
    disease_outcome: diary.diseaseOutcome,
    guarantees: diary.guarantees,
    icd_id: diary.icdId,
    is_filed: diary.isFiled,
    objectively: diary.objectively,
    prognosis: diary.prognosis,
    recommendations: diary.recommendations,
    sanitized: diary.sanitized,
    severity_condition: diary.severityCondition,
    status_lokalis: diary.statusLokalis,
    studies: diary.studies,
    treatment: diary.treatment,
    template_id: diary.templateId,
    teeth: diary.teeth,
    dental_parts: diary.dentalParts,
  });
}
/**
 * @description Запрос на редактирование дневника приема.
 * @param {number} visitId Идентификатор визита.
 * @param {Diary}  diary   Дневник приема.
 */
export function EditVisitDiaryRequest(visitId, diary) {
  this.getVisitId = () => visitId;
  this.getDiaryId = () => diary.id;
  this.toQuery = () => ({
    complaints: diary.complaints,
    dental_anamnesis: diary.dentalAnamnesis,
    diagnosis: diary.diagnosis,
    disease_outcome: diary.diseaseOutcome,
    guarantees: diary.guarantees,
    icd_id: diary.icdId,
    is_filed: diary.isFiled,
    objectively: diary.objectively,
    prognosis: diary.prognosis,
    recommendations: diary.recommendations,
    sanitized: diary.sanitized,
    severity_condition: diary.severityCondition,
    status_lokalis: diary.statusLokalis,
    studies: diary.studies,
    treatment: diary.treatment,
    template_id: diary.templateId,
    teeth: diary.teeth,
    dental_parts: diary.dentalParts,
  });
}
/**
 * @description Запрос на создание визита.
 * @param {Tooth[]}      dentalFormula
 * @param {array}        diaries
 * @param {number}       patientId
 * @param {ServiceTable} serviceTable
 * @param {array}        workOrderTable
 * @param {PatientVisit} visit
 * @constructor
 */
export function CreateVisitRequest(
  dentalFormula,
  diaries,
  patientId,
  serviceTable,
  workOrderTable,
  visit,
) {
  this.getPatientId = () => patientId;
  this.toQuery = () => ({
    dental_formula: dentalFormula.map((tooth) => ({
      number: tooth.number,
      parts: tooth.parts.map((part) => ({
        id: part.id,
        diagnoses: part.diagnoses.map((diagnosis) => diagnosis.id),
      })),
    })),
    diaries: diaries.map((diary) => ({
      complaints: diary.complaints,
      dental_anamnesis: diary.dentalAnamnesis,
      diagnosis: diary.diagnosis,
      disease_outcome: diary.diseaseOutcome,
      guarantees: diary.guarantees,
      icd_id: diary.icdId,
      is_filed: diary.isFiled,
      objectively: diary.objectively,
      prognosis: diary.prognosis,
      recommendations: diary.recommendations,
      severity_condition: diary.severityCondition,
      status_lokalis: diary.statusLokalis,
      studies: diary.studies,
      treatment: diary.treatment,
      template_id: diary.templateId,
    })),
    services: serviceTable.rows.map((item) => ({
      by_vhi_policy: item.byVHIPolicy,
      code_icd: item.code,
      count: item.count,
      discount: item.discount,
      duration: item.duration,
      price: item.price,
      service_id: item.serviceId,
      teeth: item.teeth,
      units: item.units,
    })),
    visit: {
      date: getISODateStringByDate(visit.date),
      employee_id: visit.employeeId,
      ends_time: visit.endsTime,
      occlusion: visit.occlusion,
      nsi_condition_severenity_id: visit.nsiConditionSeverenity,
      starts_time: visit.startsTime,
      sanitized: visit.sanitized,
      comment: visit.comment,
    },
    workOrder: workOrderTable,
  });
}

/**
 * @description Визит пациента.
 * @param {Date}        date
 * @param {number|null} employeeId
 * @param {string|null} employeeName
 * @param {string}      endsTime
 * @param {string}      occlusion
 * @param {boolean}     isEditDate
 * @param {string}      name
 * @param {string}      nsiConditionSeverenity
 * @param {string}      nsiMedicalCarePaymentSource
 * @param {string}      startsTime
 * @param {boolean}     sanitized
 * @param {string}      comment
 * @constructor
 */
export function PatientVisit(
  date = new Date(),
  employeeId = null,
  employeeName = null,
  endsTime = '00:00',
  occlusion = '',
  isEditDate = true,
  name = 'Новый визит',
  nsiConditionSeverenity = '',
  nsiMedicalCarePaymentSource = '',
  startsTime = '00:00',
  sanitized = false,
  comment = '',
) {
  this.date = date;
  this.employeeId = employeeId;
  this.endsTime = endsTime;
  this.occlusion = occlusion;
  this.isEditDate = isEditDate;
  this.name = name;
  this.nsiConditionSeverenity = nsiConditionSeverenity;
  this.nsiMedicalCarePaymentSource = nsiMedicalCarePaymentSource;
  this.startsTime = startsTime;
  this.sanitized = sanitized;
  this.comment = comment;
  this.getDoctor = () => ({
    id: employeeId,
    name: employeeName,
  });
  let initial = JSON.stringify(this);
  let initialSanitized = sanitized;
  this.refresh = () => {
    initial = JSON.stringify(this);
    initialSanitized = sanitized;
  };
  this.sanitizedIsChanged = () => this.sanitized !== initialSanitized;
  this.isChanged = () => JSON.stringify(this) !== initial;
}

/**
 * @description Запрос на редактирование визита.
 * @param {boolean} sanitized
 * @param {string}  occlusion
 * @param {string}  nsiConditionSeverenity
 * @param {string}  nsiMedicalCarePaymentSource
 * @param {string}  comment
 * @param {number}  visitId
 * @param {CrmDealOptions|null}  crmDealOptions
 * @constructor
 */
export function EditPatientVisitRequest(
  sanitized,
  occlusion,
  nsiConditionSeverenity,
  nsiMedicalCarePaymentSource,
  comment,
  visitId,
  crmDealOptions = null,
) {
  this.getVisitId = () => visitId;
  this.toQuery = () => ({
    occlusion,
    nsi_condition_severenity_id: nsiConditionSeverenity,
    nsi_medical_care_payment_source_id: nsiMedicalCarePaymentSource,
    sanitized,
    comment,
    crm_deal_options: crmDealOptions?.toQuery(),
  });
}

/**
 * @description Опции CRM-заявки.
 * @param {string}  stageName
 * @param {boolean} needCreate
 * @constructor
 */
export function CrmDealOptions(
  stageName = '',
  needCreate = false,
) {
  this.toQuery = () => ({
    stage_name: stageName,
    need_create: needCreate,
  });
}

/**
 * @description Шлюз визитов пациентов.
 * @param {Axios} axios HTTP клиент.
 * @constructor
 */
export default function VisitGateway(axios) {
  /**
   * @description Отправить запрос на удаление визита пациента.
   * @param {any} patientId
   * @param {any} visitId
   * @returns {Promise<Validation>}
   */
  this.delete = (patientId, visitId) => axios
    .delete(`/api/patients/${patientId}/visits/${visitId}`)
    .then(
      async (response) => Validation.Success(response.data),
      async (error) => {
        if (error.isAxiosError) {
          return Validation.Fail(error.response.data);
        }
        throw error;
      },
    );

  /**
   * @description Удалить шаблон для протокола приема.
   * @param {number} templateId Идентификатор шаблона.
   * @return {Promise<Either>}
   */
  this.deleteDiaryTemplate = (templateId) => axios
    .delete(`/api/diary-templates/${templateId}`)
    .then(
      (response) => Either.Right(response.data),
      (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          return Either.Left(error.response.data);
        }
        throw error;
      },
    );

  /**
   * @description Удалить категорию шаблонов протокола приема.
   * @param {number} categoryId Идентификатор категории.
   * @return {Promise<Either>}
   */
  this.deleteDiaryTemplateCategory = (categoryId) => axios
    .delete(`/api/diary-template-categories/${categoryId}`)
    .then(
      (response) => Either.Right(response.data),
      (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          return Either.Left(error.response.data);
        }
        throw error;
      },
    );

  /**
   * @description Отправить запрос на редактирование.
   * @param {EditVisitDiaryRequest} request Запрос на редактирование.
   * @returns {Promise<Validation>}
   */
  this.editDiary = async (request) => {
    try {
      const response = await axios.put(
        `/api/visits/${request.getVisitId()}/diaries/${request.getDiaryId()}`,
        request.toQuery(),
      );
      return Validation.Success(response.data.message);
    } catch (error) {
      if (error.isAxiosError) {
        return Validation.Fail(error.response.data);
      }
      throw error;
    }
  };

  /**
   * @description Отправить запрос на создание дневника приема.
   * @param {CreateVisitDiaryRequest} request Запрос на создание.
   * @returns {Promise<Validation>}
   */
  this.createDiary = async (request) => {
    try {
      const response = await axios.post(
        `/api/visits/${request.getVisitId()}/diaries`,
        request.toQuery(),
      );
      return Validation.Success(response.data.message);
    } catch (error) {
      if (error.isAxiosError) {
        return Validation.Fail(error.response.data);
      }
      throw error;
    }
  };

  /**
   * @description Отредактировать шаблон для протокола приема.
   * @param {number} templateId Идентификатор шаблона.
   * @param {any}    data       Данные.
   * @return {Promise<Either>}
   */
  this.editDiaryTemplate = (templateId, data) => axios
    .put(`/api/diary-templates/${templateId}`, data)
    .then(
      (response) => Either.Right(response.data),
      (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          const causes = error.response.data.causes || [];
          const { title } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );

  /**
   * @description Отредактировать шаблон для протокола приема.
   * @param {number} categoryId Идентификатор шаблона.
   * @param {string} name       Название.
   * @return {Promise<Either>}
   */
  this.editDiaryTemplateCategory = (categoryId, name) => axios
    .put(`/api/diary-template-categories/${categoryId}`, { name })
    .then(
      (response) => Either.Right(response.data),
      (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          return Either.Left(error.response.data);
        }
        throw error;
      },
    );

  /**
   * @description Скачать файл.
   * @param {number} visitId
   * @param {number} fileId
   * @returns {Promise<Validation>}
   */
  this.downloadFile = (visitId, fileId) => axios.get(`/api/visits/${visitId}/files/${fileId}`, { responseType: 'blob' }).then(
    async (response) => Validation.Success(response.data),
    async (error) => {
      if (error.isAxiosError) {
        const json = await (error.response.data || '').text();
        const data = JSON.parse(json);
        return Validation.Fail(data);
      }
      throw error;
    },
  );
  /**
   * @description Загрузить файлы.
   * @param {number} visitId
   * @param {FormData} form
   * @returns {Promise<Validation>}
   */
  this.uploadFiles = (visitId, form) => axios.post(`/api/visits/${visitId}/files`, form).then(
    async (response) => Validation.Success(response.data),
    async (error) => {
      if (error.isAxiosError) {
        return Validation.Fail(error.response.data);
      }
      throw error;
    },
  );
  /**
   * @description Удалить файлы.
   * @param {number}   visitId Идентификатор визита.
   * @param {number[]} fileIds Идентификаторы файлов.
   * @returns {Promise<Validation>}
   */
  this.deleteFiles = (visitId, fileIds) => axios
    .delete(`/api/visits/${visitId}/files`, { data: { file_ids: fileIds } })
    .then(
      async (response) => Validation.Success(response.data),
      async (error) => {
        if (error.isAxiosError) {
          return Validation.Fail(error.response.data);
        }
        throw error;
      },
    );
  /**
   * @description Скачать файл.
   * @param {number} visitId Идентификатор визита.
   * @param {number} fileId  Идентификатор визита.
   * @returns {Promise<Validation>}
   */
  this.downloadPatientFile = (visitId, fileId) => axios
    .get(`/api/visits/${visitId}/files/${fileId}`, { responseType: 'blob' })
    .then(
      async (response) => Validation.Success(response.data),
      async (error) => {
        const json = await (error.response.data || '').text();
        const data = JSON.parse(json);
        if (error.isAxiosError && error.response.status === 400) {
          const violations = data || [];
          return Validation.Fail(violations);
        }
        if (error.isAxiosError && error.response.status === 404) {
          const { causes, title } = data;
          const isNotFoundPatient = causes.some((cause) => cause.source === 'patient');
          return Validation.Fail({ title, redirect: isNotFoundPatient });
        }
        throw error;
      },
    );
  /**
   * @description Отправить запрос на изменение услуг визита.
   * @param {number}       visitId      Идентификатор визита.
   * @param {Invoices}     invoices     Счета визита.
   * @returns {Promise<Validation>}
   */
  this.editServices = async (visitId, invoices) => {
    try {
      const response = await axios
        .put(`/api/visits/${visitId}/services`, invoices.toQuery());
      return Validation.Success(response.data);
    } catch (error) {
      if (error.isAxiosError) {
        return Validation.Fail(error.response.data);
      }
      throw error;
    }
  };
  /**
   * @description Отправить запрос на изменение заказ-наряда.
   * @param {number}          visitId        Идентификатор визита.
   * @param {WorkOrderTable}  workOrderTable Таблица заказ-наряда.
   * @returns {Promise<Validation>}
   */
  this.editWorkOrder = async (visitId, workOrderTable) => {
    try {
      const response = await axios
        .put(`/api/visits/${visitId}/work-orders`, workOrderTable.toQuery());
      return Validation.Success(response.data);
    } catch (error) {
      if (error.isAxiosError) {
        return Validation.Fail(error.response.data);
      }
      throw error;
    }
  };
  /**
   * Отправить запрос на удаление услуги.
   * @param {number} visitId     Идентификатор визита.
   * @param {number} workOrderId Идентификатор заказ-наряда.
   * @returns {Promise<Validation>}
   */
  this.deleteWorkOrder = async (visitId, workOrderId) => {
    try {
      const response = await axios.delete(`/api/visits/${visitId}/work-orders/${workOrderId}`);
      return Validation.Success(response.data.message);
    } catch (error) {
      if (error.isAxiosError) {
        return Validation.Fail(error.response.data);
      }
      throw error;
    }
  };
  /**
   * Отправить запрос на удаление услуги.
   * @param {number} invoiceId Идентификатор визита.
   * @param {number} visitId   Идентификатор визита.
   * @param {number} serviceId Идентификатор услуги.
   * @returns {Promise<Validation>}
   */
  this.deleteService = async (invoiceId, visitId, serviceId) => {
    try {
      const response = await axios.delete(`/api/visits/${visitId}/services/${serviceId}`);
      return Validation.Success(response.data.message);
    } catch (error) {
      if (error.isAxiosError) {
        return Validation.Fail(error.response.data);
      }
      throw error;
    }
  };
  /**
   * @description Отправить запрос на редактирование.
   * @param {EditPatientVisitRequest} request
   * @returns {Promise<Validation>}
   */
  this.editVisit = async (request) => {
    try {
      const response = await axios.put(`/api/visits/${request.getVisitId()}`, request.toQuery());
      return Validation.Success(response.data);
    } catch (error) {
      if (error.isAxiosError) {
        return Validation.Fail(error.response.data);
      }
      throw error;
    }
  };

  /**
   * @description Получить шаблоны для протокола приема.
   * @returns {Promise<Either>}
   */
  this.getDiaryTemplates = () => axios
    .get('/api/diary-templates')
    .then(async (response) => (response.data.data));

  /**
   * @description Получить шаблоны для протокола приема.
   * @param {number|null} categoryId
   * @returns {Promise<Either>}
   */
  this.getDiaryTemplatesByCategory = (categoryId) => axios
    .get(`/api/diary-templates/categories/${categoryId}`)
    .then(async (response) => (response.data.data));

  /**
   * @description Получить шаблоны для протокола приема.
   * @returns {Promise<List<any>>}
   */
  this.getDiaryTemplateCategoryList = () => axios
    .get('/api/diary-template-categories')
    .then(async (response) => (response.data.data));

  /**
   * @description Создать шаблон для протокола приема.
   * @param {any} request Данные.
   * @return {Promise<Either>}
   */
  this.createDiaryTemplate = (request) => axios
    .post('/api/diary-templates', request)
    .then(
      (response) => Either.Right(response.data),
      (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          const causes = error.response.data.causes || [];
          const { title } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );

  /**
   * @description Создать категорию шаблонов для протокола приема.
   * @param {string} name Название.
   * @return {Promise<Either>}
   */
  this.createDiaryTemplateCategory = (name) => axios
    .post('/api/diary-template-categories', { name })
    .then(
      (response) => Either.Right(response.data),
      (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          return Either.Left(error.response.data);
        }
        throw error;
      },
    );

  /**
   * @description Создать визит.
   * @param {CreateVisitRequest} request
   * @returns {Promise<Validation>}
   */
  this.createVisit = async (request) => {
    try {
      const response = await axios.post(
        `/api/patients/${request.getPatientId()}/new-visit`,
        request.toQuery(),
      );
      return Validation.Success(response.data);
    } catch (error) {
      if (error.isAxiosError) {
        return Validation.Fail(error.response.data);
      }
      throw error;
    }
  };

  /**
   * @description Отправить запрос на редактирование зубной формулы.
   * @param {number}  visitId                 Идентификатор визита.
   * @param {Array}   teeth                   Зубы.
   * @param {boolean} isMilkBite              Молочный прикус.
   * @param {boolean} isDetailedDentalFormula Детальная зубная формула.
   * @returns {Promise<Validation>}
   */
  this.editDentalFormula = (visitId, teeth, isMilkBite, isDetailedDentalFormula) => axios
    .put(
      `/api/visits/${visitId}/dental-formula`,
      {
        teeth,
        is_milk_bite: isMilkBite,
        is_detailed_dental_formula: isDetailedDentalFormula,
      },
    )
    .then(
      async (response) => Validation.success(response.data.data),
      async (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          return Validation.fail(error.response.data);
        }
        throw error;
      },
    );

  /**
   * @description Отправить запрос на редактирование полости рта пациента.
   * @param {EditPatientDentalCavitiesRequest} request Запрос.
   */
  this.editDentalCavities = (request) => axios
    .put(`/api/visits/${request.getVisitId()}/dental-cavities`, request.toQuery())
    .then(
      async (response) => Validation.success(response.data),
      async (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          return Validation.fail(error.response.data);
        }
        throw error;
      },
    );

  /**
   * @description Сохранить визит и сформировать черновик ЕГИСЗ.
   * @param {EditPatientVisitRequest} request
   * @returns {Promise<Validation>}
   */
  this.editVisitAndFormingEgiszDraft = async (request) => {
    try {
      const response = await axios
        .put(
          `/api/visits-and-forming-egisz-draft/${request.getVisitId()}`,
          request.toQuery(),
        );
      return Validation.Success(response.data);
    } catch (error) {
      if (error.isAxiosError) {
        return Validation.Fail(error.response.data);
      }
      throw error;
    }
  };

  /**
   * @description Получить список визитов.
   *
   * @param {string} patientId
   * @param {string} nameService
   * @param {string} numberTeeth
   *
   * @returns {Promise<Either>}
   */
  this.viewList = async (patientId, nameService = '', numberTeeth = '') => {
    try {
      const response = await axios
        .get(`/api/patients/${patientId}/visits`, {
          params: {
            number_teeth: numberTeeth,
            name_service: nameService,
          },
        });
      const toValue = (fieldKey) => {
        const items = fieldKey.split('_');
        return items.shift() + items
          .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
          .join('');
      };
      return Either.right(
        {
          columns: response.data.columns.map(
            (item) => ({
              id: item.id,
              actions: item.actions,
              title: item.title,
              fieldKey: toValue(item.field_key),
              isVisible: item.is_visible,
            }),
          ),
          visits: response.data.data.map((item) => ({
            appointmentId: item.appointment_id,
            date: item.date,
            debt: item.debt,
            employeeFullName: item.employee_full_name,
            employeeSpeciality: item.employee_speciality,
            employeeSurnameWithInitials: item.employee_surname_with_initials,
            id: item.id,
            iemkStatus: makeStatusBySchema(item.iemk_status),
            chiefPhysicianStatus: makeChiefStatusBySchema(item.chief_physician_status),
            serviceNames: item.service_names,
            title: item.title,
            moneyValue: item.money_value,
            moneyTitle: item.money_title,
            visitTime: item.visit_time,
            visitEndTime: item.visit_end_time,
            dateTitle: item.date_title,
            branchName: item.branch_name,
            teeth: item.teeth,
          })),
        },
      );
    } catch (error) {
      if (error.isAxiosError) {
        return Either.left(error.response.data);
      }
      throw error;
    }
  };
  /**
   * @description Получить визит пациента по идентификатору.
   * @param {number} patientId Идентификатор пациента.
   * @param {number} visitId   Идентификатор визита.
   * @returns {Promise<Validation>}
   */
  this.viewVisitById = async (patientId, visitId) => {
    try {
      const response = await axios.get(`/api/patients/${patientId}/visits/${visitId}`);
      const { data } = response;
      const diaries = data.diaries.map((diary) => makeDiaryByScheme(diary));
      const files = data.files.map((file) => ({ id: file.id, name: file.name }));
      const visit = new PatientVisit(
        new Date(data.visit.date),
        data.visit.employee_id,
        data.visit.employee_name,
        data.visit.ends_time,
        data.occlusion,
        data.visit.is_allowed_edit_date,
        data.visit.title,
        data.nsi_condition_severenity_id,
        data.nsi_medical_care_payment_source_code,
        data.visit.starts_time,
        data.visit.is_sanitized,
        data.visit.comment,
      );
      const invoices = makeInvoicesByScheme(data.invoices);
      const toothList = makeDentalFormulaTeeth(data.dental_formula);
      const dentalCavities = makeDentalCavities(data.dental_cavities.teeth);
      const documents = data.documents.map((document) => ({
        id: document.id,
        name: document.name,
        count: 1,
        isPrint: document.id === 'protocol',
        number: document.number,
        date: data.visit.date,
      }));
      const downloadDocuments = data
        .documents
        .filter((item) => item.id !== 'form043y')
        .map((document) => ({
          id: document.id,
          name: document.name,
          isPrint: false,
          number: document.number,
          date: data.visit.date,
        }));
      const workOrder = data.work_order.map((row) => ({
        id: row.id,
        serviceName: row.service_name,
        teeth: row.teeth,
        dentalParts: row.dental_parts || [],
        count: row.count,
        price: row.price,
        vita: row.vita,
      }));
      return Validation.Success({
        actions: data.actions,
        chiefPhysicianStatus: makeChiefStatusBySchema(data.chief_physician_status),
        diaries,
        diariesTitle: data.diaries_title,
        discountType: data.discount_type,
        documents,
        files,
        iemkStatus: makeStatusBySchema(data.iemk_status),
        inactiveActions: data.inactive_actions,
        invoiceActions: data.invoice_actions,
        invoiceId: data.invoice_id,
        isMilkBite: data.is_milk_bite,
        isDetailedDentalFormula: data.is_detailed_dental_formula,
        invoices,
        toothList,
        visit,
        workOrder,
        dentalCavities,
        currentCrmDealStageName: data.current_crm_deal_stage_name,
        downloadDocuments,
        hasGuardian: data.has_guardian,
      });
    } catch (error) {
      if (error.isAxiosError) {
        return Validation.Fail(error.response.data);
      }
      throw error;
    }
  };
  /**
   * @description Получить пустую зубную формулу.
   * @returns {Promise<Maybe>}
   */
  this.defaultDentalFormula = async () => {
    const response = await axios.get('/api/dental-formula');
    const toothList = response.data.dental_formula.map(
      (tooth) => new Tooth(
        tooth.number,
        tooth.parts.map(
          (part) => new Part(
            part.id,
            part.name,
            part.diagnoses.map(
              (diagnosis) => new Diagnosis(diagnosis.id, diagnosis.name, diagnosis.color),
            ),
          ),
        ),
      ),
    );
    return Maybe.Some(new DentalFormulaFactory(toothList));
  };
  /**
   * @description Получить последнюю зубную формулу пациента.
   * @param {number} patientId Идентификатор пациента.
   * @returns {Promise<Validation>}
   */
  this.patientLastDentalFormula = async (patientId) => {
    try {
      const response = await axios.get(`/api/patients/${patientId}/dental-formula`);
      const toothList = response.data.dental_formula.map(
        (tooth) => new Tooth(
          tooth.number,
          tooth.parts.map(
            (part) => new Part(
              part.id,
              part.name,
              part.diagnoses.map(
                (diagnosis) => new Diagnosis(diagnosis.id, diagnosis.name, diagnosis.color),
              ),
            ),
          ),
        ),
      );
      return Validation.Success(new DentalFormulaFactory(toothList));
    } catch (error) {
      if (error.isAxiosError && error.response.status === 400) {
        const { title } = error.response.data;
        return Validation.Fail({ title });
      }
      if (error.isAxiosError && error.response.status === 404) {
        const { title } = error.response.data;
        return Validation.Fail({ title });
      }
      throw error;
    }
  };
  /**
   * @description Получить список услуг визита.
   * @param {number} visitId
   * @returns {Promise<Either>}
   */
  this.viewVisitServiceList = (visitId) => axios
    .get(`/api/visits/${visitId}/services`)
    .then(
      async (response) => {
        const { data } = response;
        const invoices = makeInvoicesByScheme(data.data);
        return Either.right({
          invoices,
        });
      },
      async (error) => {
        if (error.isAxiosError && error.response.status === 400) {
          return Either.left(error.response.data.title);
        }
        throw error;
      },
    );
  /**
   * @description Получить список болезней по МКБ-10
   * @param {string} query Название болезни.
   * @param {string} category Категория.
   * @returns {Promise<Maybe>}
   */
  this.viewDiseaseList = async (query, category) => {
    const response = await axios.get('/api/diseases-icd', {
      params: {
        query,
        category,
      },
    });
    return Maybe.Some(response.data.data);
  };
  /**
   * @description Получить список категорий болезней по МКБ-10
   * @param {string} category Категория.
   * @returns {Promise<Maybe>}
   */
  this.viewDiseaseCategoryList = async (category) => {
    const response = await axios.get('/api/diseases-icd/categories', {
      params: {
        category,
      },
    });
    const list = (response.data.data || []).map((item) => ({
      id: item.id,
      name: item.name,
      children: [...item.children, ...item.diseases].sort((a, b) => a.name.localeCompare(b.name, ['en', 'ru'])),
    }))
      .sort((a, b) => a.name.localeCompare(b.name, ['en', 'ru']));
    return Maybe.Some(list);
  };

  /**
   * @description Получить документ для скачивания.
   * @param {DownloadDocumentRequest} request Запрос на скачивание.
   * @returns {Promise<Either>}
   */
  this.viewDocumentsForDownload = (request) => axios
    .get(
      `/api/visits/${request.getVisitId()}/download`,
      {
        responseType: 'blob',
        params: request.toQuery(),
      },
    )
    .then(
      async (response) => Either.right(response.data),
      async (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          const { title } = error.response.data;
          const leftResponse = new LeftResponse([], title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );

  /**
   * @description Получить документы для печати.
   * @param {PrintDocumentsRequest} request Запрос на печать.
   * @returns {Promise<Either>}
   */
  this.viewDocumentsForPrint = (request) => axios
    .get(`/api/visits/${request.getVisitId()}/print`, { params: request.toQuery() })
    .then(
      async (response) => Either.right(response.data),
      async (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          const { title } = error.response.data;
          const leftResponse = new LeftResponse([], title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );

  /**
   * @description Получить зубную формулу для печати.
   * @param {number}  visitId     Идентификатор визита.
   * @param {boolean} isBabyTeeth Молочный прикус.
   * @returns {Promise<Either>}
   */
  this.viewVisitFormulaTeethPrint = (visitId, isBabyTeeth) => axios
    .get(`/api/visits/${visitId}/formula-teeth-print`, { params: { is_baby_teeth: isBabyTeeth } })
    .then(
      async (response) => Either.right(response.data),
      async (error) => {
        if (error.isAxiosError && [400, 403, 404].includes(error.response.status)) {
          const { title } = error.response.data;
          const leftResponse = new LeftResponse([], title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );

  /**
   * @description Получить данные для заполнения визита.
   * @param {number} patientId Идентификатор пациента.
   * @returns {Promise<Maybe>}
   */
  this.viewVisitFillers = (patientId) => axios
    .get(`/api/patients/${patientId}/visit-fillers`)
    .then(
      async (response) => {
        const { data } = response.data;
        const makeService = (service) => ({
          categoryId: service.category_id,
          categoryName: service.category_name,
          cost: service.cost,
          duration: service.duration,
          id: service.id,
          name: service.name,
          units: service.units,
        });
        const makeCategoryList = (category) => new Category(
          category.id,
          category.name,
          category.services.map(makeService),
          category.children,
        );
        const priceLists = (data.prices || [])
          .map((price) => new PriceList(
            price.id,
            price.name,
            price.categories.map((category) => makeCategoryList(category)),
          ));
        const categories = (data.categories || [])
          .map((category) => makeCategoryList(category));
        const makeTreatmentPlanService = (service) => ({
          byVHIPolicy: service.by_vhi_policy,
          categoryId: service.category_id,
          categoryName: service.category_name,
          cost: service.cost,
          count: service.count,
          dentalParts: service.dental_parts,
          discount: service.discount,
          discountType: service.discount_type,
          id: service.id,
          name: service.name,
          serviceId: service.service_id,
          teeth: service.teeth,
          units: service.units,
        });
        const makeTreatmentPlanCategory = (plan) => new Category(
          plan.id,
          plan.name,
          plan.services.map(makeTreatmentPlanService),
          plan.children,
          plan.stages.map((stage) => ({
            id: stage.id,
            name: stage.name,
            services: stage.services.map(makeTreatmentPlanService),
          })),
        );
        const dentalFormulaDiagnoses = Object.fromEntries(
          data.dental_formula_diagnoses.map((part) => [
            part.id,
            part.diagnoses.map((diagnosis) => ({
              id: diagnosis.id,
              name: diagnosis.name,
              color: diagnosis.color,
            })),
          ]),
        );
        const nsiConditionSeverenities = (data.nsi_condition_severenities || [])
          .map((item) => ({
            title: item.name,
            value: item.id,
          }));
        const nsiMedicalCarePaymentSources = (data.nsi_medical_care_payment_sources || [])
          .map((item) => ({
            tags: item.tags || [],
            title: item.title,
            value: item.code,
          }));
        const treatmentPlanList = (data.treatment_plans || [])
          .map((plan) => (makeTreatmentPlanCategory(plan)));
        return Maybe.some({
          priceLists,
          dentalFormulaDiagnoses,
          nsiConditionSeverenities,
          nsiMedicalCarePaymentSources,
          treatmentPlanList,
          categories,
        });
      },
    );
}
